// OYU Fest API — talks to the hero-payment service (`/pay/api/oyufest`).
// Identity is proven via SMS-OTP (request-code → verify) and carried in a
// festival JWT stored on-device; the server derives the participant from that
// token, never from a phone in the request. The `phone` still present in some
// call signatures/URLs is vestigial navigation state and is ignored server-side.
const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "https://admin.herosjourney.kz/pay/api/oyufest";

// ---- session token ----
const TOKEN_KEY = "oyufest_token";
export const getToken = (): string => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};
export const setToken = (t: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {
    /* ignore */
  }
};
export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};
export const isAuthed = (): boolean => !!getToken();

// Staff access code (coach panel + TV leaderboard). Stored per-device; attached
// to every request (harmless on open routes, required on operator-only ones).
export type StaffRole = "admin" | "marketing" | "coach" | "redbull";
const STAFF_KEY = "oyufest_staff";
const STAFF_ROLE_KEY = "oyufest_staff_role";
export const getStaffCode = (): string => {
  try {
    return localStorage.getItem(STAFF_KEY) || "";
  } catch {
    return "";
  }
};
export const setStaffCode = (code: string) => {
  try {
    localStorage.setItem(STAFF_KEY, code);
  } catch {
    /* ignore */
  }
};
export const clearStaffCode = () => {
  try {
    localStorage.removeItem(STAFF_KEY);
    localStorage.removeItem(STAFF_ROLE_KEY);
  } catch {
    /* ignore */
  }
};
export const getStaffRole = (): StaffRole | null => {
  try {
    return (localStorage.getItem(STAFF_ROLE_KEY) as StaffRole) || null;
  } catch {
    return null;
  }
};
export const setStaffRole = (role: StaffRole) => {
  try {
    localStorage.setItem(STAFF_ROLE_KEY, role);
  } catch {
    /* ignore */
  }
};

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type ReqInit = {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
};

async function req<T>(path: string, init?: ReqInit): Promise<T> {
  const token = getToken();
  const staff = getStaffCode();
  const r = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(staff ? { "x-oyufest-staff": staff } : {}),
      ...(init?.headers || {}),
    },
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) {
    const code = (data?.error as string) || "error";
    // Guest JWT expired/invalid → drop it and bounce to login so a shared device
    // can re-auth cleanly. Skip staff routes (their 401 is a bad staff code, not
    // token expiry) and avoid a redirect loop on /login itself.
    const isStaffPath = /^\/(coach|admin|staff|leaderboard)/.test(path);
    if (r.status === 401 && !isStaffPath) {
      clearToken();
      try {
        const base = import.meta.env.BASE_URL || "/";
        if (!location.pathname.endsWith("/login"))
          location.assign(`${base}login`);
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(r.status, code, code);
  }
  return data as T;
}
const post = <T>(path: string, body: unknown) =>
  req<T>(path, { method: "POST", body: JSON.stringify(body) });
const get = <T>(path: string) => req<T>(path);

// ---- types ----
export interface ClanMember {
  nickname: string | null;
  role: "leader" | "member";
}
export interface ClanTask {
  key: string;
  title: string;
  done: boolean;
  role?: StaffRole | null;
}
export interface HubClan {
  id: string;
  code: string;
  name: string;
  motto: string | null;
  emoji: string | null;
  members: ClanMember[];
  memberCount: number;
  ready: boolean;
  tasks: ClanTask[];
  tasksDone: number;
  tasksTotal: number;
  completed: boolean;
}
export interface Hub {
  // `sex` нужен фронту для дефолтной вкладки лидерборда (Ж/М); бэку надо отдавать его в /hub.
  // `isExistingClient` — у гостя была хотя бы 1 оплата HJ: другой дизайн + приз (подарочный ХВ).
  participant: {
    id: string;
    nickname: string;
    phone: string;
    sex?: Sex | null;
    isExistingClient?: boolean;
  };
  clan: HubClan | null;
  rowing: { distance_m: number; power_w: number } | null;
  prize: { eligible: boolean; granted: boolean };
}
// Публичная карточка клана (список «Кланы фестиваля» — без кода приглашения).
export interface PublicClan {
  id: string;
  name: string;
  motto: string | null;
  emoji: string | null;
  memberCount: number;
  ready: boolean;
  tasksDone: number;
  tasksTotal: number;
  completed: boolean;
}
export interface Club {
  id: string;
  name: string;
  city?: string | null;
  address?: string | null;
}
export interface InBodyTeaser {
  hasResult: boolean;
  score?: number;
  pbf?: number;
  // Метаболический возраст с аппарата InBody — показывается открыто.
  metabolicAge?: number;
  testDate?: string;
}
export interface CoachClan {
  id: string;
  code: string;
  name: string;
  emoji: string | null;
  leader: { nickname: string | null; phone: string | null } | null;
  members: ClanMember[];
  memberCount: number;
  ready: boolean;
  tasks: ClanTask[];
  tasksDone: number;
  tasksTotal: number;
  completed: boolean;
}
export type Sex = "M" | "F";
export interface LeaderboardRow {
  nickname: string | null;
  sex: Sex | null;
  distance_m: number;
  power_w: number;
  recorded_at?: string | null;
}
export interface AdminStats {
  participants: {
    total: number;
    men: number;
    women: number;
    unspecified: number;
  };
  clans: { total: number; ready: number; completed: number };
  members: { total: number };
  rowing: {
    count: number;
    withPrize: number;
    top: {
      nickname: string | null;
      distance_m: number;
      power_w: number;
    } | null;
  };
  prizes: { total: number };
  tasks: {
    key: string;
    title: string;
    role: StaffRole | null;
    clansDone: number;
  }[];
}
export interface AdminTask {
  key: string;
  title: string;
  sort: number;
  role: StaffRole | null;
  is_active: boolean;
}
export interface WinnerRow {
  rank: number;
  nickname: string | null;
  phone: string | null;
  distance_m: number;
  power_w: number;
  recorded_at?: string | null;
}

export interface AuthParticipant {
  id: string;
  nickname: string;
  phone: string;
  sex?: Sex | null;
  isExistingClient?: boolean;
}
type AuthResp = { token: string; participant: AuthParticipant };

// ---- auth ----
// Open registration (marketing dropped the SMS gate). The server finds-or-creates
// the participant from the phone and mints a session token; existing paying
// clients come back flagged so the UI shows their gift-prize variant.
export const register = async (phone: string, nickname?: string, sex?: Sex) => {
  const r = await post<AuthResp>("/auth/register", { phone, nickname, sex });
  if (r.token) setToken(r.token);
  return r;
};

// Смена ника в профиле; сервер проверяет уникальность (код ошибки nick_taken).
export const updateNickname = (nickname: string) =>
  post<{ ok: boolean }>("/profile/nickname", { nickname });

// SMS-OTP path — kept as a still-wired fallback, no longer used by the UI.
export const requestCode = (phone: string) =>
  post<{ ok: boolean }>("/auth/request-code", { phone });
export const verifyCode = async (
  phone: string,
  code: string,
  nickname?: string,
  sex?: Sex,
) => {
  const r = await post<AuthResp>("/auth/verify", {
    phone,
    code,
    nickname,
    sex,
  });
  if (r.token) setToken(r.token);
  return r;
};

// ---- скрытие записей на фронте ----
// 1) Тестовые записи от проверок 2–3 июля (гребля + клан «Клан Клауда») — бэк
//    их пока не почистил.
// 2) vanda03 — мужчина, по ошибке зарегистрировавшийся женщиной и занявший 1-е
//    место в женском зачёте. Пол фиксируется на бэке и меняется только правкой
//    в базе; до неё прячем его во всех списках, чтобы не искажал женский топ.
// Ники уникальны на весь фестиваль, так что настоящий гость их занять не может.
const HIDDEN_NICKS = new Set(["guest_uabq3p", "sheesh", "guest_i80ain", "dias", "vanda03"]);
const HIDDEN_TEST_CLAN_IDS = new Set(["6a463755a7323b55aa2f3da9"]); // «Клан Клауда»
const isTestNick = (n?: string | null) => !!n && HIDDEN_NICKS.has(n.toLowerCase());

// ---- guest flow (identity from the token) ----
export const getHub = () => get<Hub>("/hub");
export const clanCreate = (name: string, motto?: string, emoji?: string) =>
  post<{ clan: { id: string; code: string; name: string } }>("/clan/create", {
    name,
    motto,
    emoji,
  });
export const clanJoin = (code: string) =>
  post<{ clan: { id: string; code: string; name: string } }>("/clan/join", {
    code,
  });
export const listClans = () =>
  get<{ clans: PublicClan[] }>("/clans/list").then((r) => ({
    clans: r.clans.filter((c) => !HIDDEN_TEST_CLAN_IDS.has(c.id)),
  }));

// ---- сторис-задание (личная сдача каждым участником клана) ----
export type StoryStatus = "none" | "pending" | "approved";
export interface StoryTaskStatus {
  mine: { status: StoryStatus; instagram: string | null };
  members: { nickname: string | null; status: StoryStatus }[];
  approved: number;
  total: number;
}
export const storyTaskStatus = () =>
  get<StoryTaskStatus>("/tasks/logo_story/status");
export const storyTaskSubmit = (instagram: string) =>
  post<{ ok: boolean }>("/tasks/logo_story/submit", { instagram });
export const getClubs = () => get<{ clubs: Club[] }>("/clubs");
// `gift: true` — existing client claimed a gift Hero's Week (no club/date needed).
export const prizeGrant = (clubId?: string, startDate?: string) =>
  post<{
    granted?: boolean;
    alreadyGranted?: boolean;
    gift?: boolean;
    marathonEventId?: string;
  }>("/prize/grant", { clubId, startDate });
export const getInBody = () => get<InBodyTeaser>("/inbody");

// ---- coach panel (staff code) ----
export const coachClans = (q?: string) =>
  get<{ clans: CoachClan[] }>(
    `/coach/clans${q ? `?q=${encodeURIComponent(q)}` : ""}`,
  ).then((r) => ({
    clans: r.clans.filter((c) => !HIDDEN_TEST_CLAN_IDS.has(c.id)),
  }));
export const coachCheck = (clanId: string, taskKey: string, done: boolean) =>
  post<{
    clanId: string;
    ready: boolean;
    tasksDone: number;
    tasksTotal: number;
    completed: boolean;
  }>("/coach/check", { clanId, taskKey, done });
// Результат гребли записывается по телефону ИЛИ по нику (ники уникальны);
// повторная запись тому же участнику перезаписывает результат.
export const coachRowing = (target: { phone?: string; nickname?: string }, distanceM: number) =>
  post<{ distance_m: number; power_w: number }>("/coach/rowing", { ...target, distance_m: distanceM });
export const getLeaderboard = () =>
  get<{ leaderboard: LeaderboardRow[] }>("/leaderboard").then((r) => ({
    leaderboard: r.leaderboard.filter((row) => !isTestNick(row.nickname)),
  }));
export const staffAuth = (code: string) =>
  post<{ ok: boolean; role?: StaffRole }>("/staff/auth", { code });

// ---- admin (organizer only) ----
export const adminStats = () =>
  get<AdminStats>("/admin/stats").then((s) =>
    s.rowing?.top && isTestNick(s.rowing.top.nickname)
      ? { ...s, rowing: { ...s.rowing, top: null } }
      : s,
  );
export const adminWinners = () =>
  get<{ women: WinnerRow[]; men: WinnerRow[] }>("/admin/winners").then((r) => {
    // После вырезания тестовых строк места пересчитываются заново.
    const clean = (rows: WinnerRow[]) =>
      rows.filter((w) => !isTestNick(w.nickname)).map((w, i) => ({ ...w, rank: i + 1 }));
    return { women: clean(r.women), men: clean(r.men) };
  });
export interface StorySubmission {
  phone: string;
  nickname: string | null;
  instagram: string;
  clan: string | null;
  status: "pending" | "approved";
}
export const staffStorySubmissions = () =>
  get<{ submissions: StorySubmission[] }>("/staff/story-submissions");
export const staffStoryReview = (phone: string, approve: boolean) =>
  post<{ ok: boolean }>("/staff/story-review", { phone, approve });
// Полный список гребли с телефонами (только для организатора). Пока бэк не
// отдаёт /admin/rowing, админка падает обратно на публичный /leaderboard.
export interface AdminRowingRow extends LeaderboardRow {
  phone?: string | null;
}
export const adminRowing = () =>
  get<{ results: AdminRowingRow[] }>("/admin/rowing").then((r) => ({
    results: r.results.filter((row) => !isTestNick(row.nickname)),
  }));

// Прошедшие InBody (Зона 2). Бэк может ещё не отдавать этот список —
// админка в этом случае показывает подсказку, а не ошибку.
export interface AdminInBodyRow {
  nickname: string | null;
  phone: string | null;
  score?: number | null;
  testDate?: string | null;
}
export const adminInBody = () =>
  get<{ results: AdminInBodyRow[] }>("/admin/inbody");

export const adminTasks = () => get<{ tasks: AdminTask[] }>("/admin/tasks");
export const adminTaskSave = (payload: {
  key?: string;
  title: string;
  role: StaffRole | null;
  sort?: number;
  is_active?: boolean;
}) => post<{ task: AdminTask }>("/admin/tasks", payload);
